import { Injectable, ComponentFactoryResolver, ComponentRef } from '@angular/core';
import { Directive, ViewContainerRef } from '@angular/core';

import { ISidebarDetail, ISidebarDetailData, SidebarDetailComponent } from '@appshared';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';

// This service will inject the entire Angular component to a place on high-level of Fuse structure, inside a form of sidebar element
// It will allows any content to occupy the whole screen with 100% height
// Since it's too abstract, need to have an wrapper. For an example please see the ReconciledDetailTicketService, which:
//    - Wraps everything inside
//    - Providing an initialize method for the Module to call to render view.

// Downfall:
// - Have to manually trigger the ngOndesotry(). Call the destroyComponent().
//    + More detail: https://stackoverflow.com/questions/60336333/ngdestroy-lifecycle-is-not-triggering-in-dynamically-created-angular-component
// - [Fixed by leveraging app-error-handler.service.ts:78] Can't display any errors in console at all.
//    + See https://stackoverflow.com/questions/56396481/how-to-catch-errors-that-happen-when-showing-an-angular-dynamic-component

@Injectable({
  providedIn: 'root',
})
export class SidebarDetailService {
  // store the HTML element which for dynamic replacing HTML template
  private sidebarDetailEle;

  // list of views
  private listView: SidebarDetailViews[] = [];

  private crrViewIdx = -1;

  constructor(private _fuseSidebarService: FuseSidebarService, private _componentFactoryResolver: ComponentFactoryResolver) {}

  public setHostEle(ele: SidebarDetailHostDirective): void {
    this.sidebarDetailEle = ele.viewContainerRef;
  }

  public getHostEle(): ViewContainerRef {
    return this.sidebarDetailEle;
  }

  private findCrrViewById(viewId): SidebarDetailViews {
    return this.listView.find(ele => {
      return viewId === ele.viewId;
    });
  }

  private findCrrViewIdxById(viewId): number {
    return this.listView.findIndex(ele => {
      return viewId === ele.viewId;
    });
  }

  // render component and return the new instance
  public renderComponent(viewId: string, sidebarComp: SidebarDetailComponent, bRerender = false): any {
    let viewIdx = this.findCrrViewIdxById(viewId);

    // if not exist, get the component and create the view
    if (viewIdx === -1 || bRerender) {
      const componentFactory = this._componentFactoryResolver.resolveComponentFactory(sidebarComp.detailComponent);

      const viewContainerRef = this.getHostEle();
      viewContainerRef.clear();

      const componentInst: ComponentRef<ISidebarDetail> = viewContainerRef.createComponent<ISidebarDetail>(componentFactory);

      // if not forced rerender, push new view
      if (!bRerender) {
        this.listView.push(new SidebarDetailViews(viewId, sidebarComp, componentInst));
        viewIdx = this.listView.length - 1;
      }
    }

    this.crrViewIdx = viewIdx;

    return this.listView[viewIdx].componentIns;
  }

  // https://stackoverflow.com/questions/60336333/ngdestroy-lifecycle-is-not-triggering-in-dynamically-created-angular-component
  public destroyComponent(viewId: string): void {
    let viewIdx = this.findCrrViewIdxById(viewId);
    if (viewIdx > -1) {
      // destroy the component first
      this.listView[viewIdx].componentIns.destroy();

      // then remove this view from the list
      this.listView.splice(viewIdx, 1);
    }
  }

  // open the viewId, if can't find inside the list, simply open the current view again
  public openView(viewId?: string): void {
    if (viewId && this.listView[this.crrViewIdx].viewId !== viewId) {
      this.renderComponent(viewId, this.listView[this.crrViewIdx].componentRef);
    }

    this._fuseSidebarService.getSidebar('detailRightSidebar').open();
  }

  public closeView(): void {
    this._fuseSidebarService.getSidebar('detailRightSidebar').close();
  }

  public isOpenView(): boolean {
    return this._fuseSidebarService?.getSidebar('detailRightSidebar')?.opened;
  }

  // notify the instance about new input data
  public sendDataToView(viewId: string, data: ISidebarDetailData): void {
    const detailView = this.findCrrViewById(viewId);
    detailView.componentIns.instance.onInputChange(data);
  }
}

@Directive({
  selector: '[sidebarDetailHost]',
})
export class SidebarDetailHostDirective {
  constructor(public viewContainerRef: ViewContainerRef) {}
}

class SidebarDetailViews {
  constructor(
    public viewId: string,

    // preference to the component class, for late construction
    public componentRef: SidebarDetailComponent,

    // the instance of generated component
    public componentIns: ComponentRef<ISidebarDetail>
  ) {}
}
